<template>
  <main class="flex justify-center bg-gray-50">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">

      <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
        <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg">{{ errorMessage }}</div>
        <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg">{{ successMessage }}</div>
      </div>

      <!-- En-tête -->
      <div class="w-full mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">Charges</h1>
          <p class="text-sm text-gray-500" v-if="!isSuper">Boutique : {{ user?.boutique }}</p>
        </div>
        <button @click="openModal()" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-md self-start">
          <i class="fa-solid fa-plus mr-2"></i> Ajouter une charge
        </button>
      </div>

      <!-- Filtres -->
      <div class="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div v-if="isSuper" class="flex overflow-x-auto">
            <button
              v-for="opt in scopeOptions"
              :key="opt.value"
              @click="setScope(opt.value)"
              :class="filters.scope === opt.value ? 'border-b-2 border-orange-500 text-orange-500' : 'border-b-2 border-transparent text-gray-500 hover:text-orange-500'"
              class="text-sm font-medium px-3 py-2 whitespace-nowrap transition-colors"
            >
              {{ opt.label }}
            </button>
          </div>
          <div class="flex flex-wrap items-end gap-3">
            <div>
              <label class="block text-xs text-gray-500 mb-1">Du</label>
              <input type="date" v-model="filters.from" @change="fetchCharges" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Au</label>
              <input type="date" v-model="filters.to" @change="fetchCharges" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
            </div>
            <button @click="resetFilters" class="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Réinitialiser</button>
          </div>
        </div>
        <div class="mt-3 text-right">
          <p class="text-xs text-gray-500">Total</p>
          <p class="text-lg font-bold text-red-500">{{ formatMoney(totalCharges) }}</p>
        </div>
      </div>

      <div v-if="isLoading" class="text-center h-[250px] w-full flex flex-col justify-center items-center">
        <div class="loader mb-4"></div>
        <p class="text-gray-500 font-medium">Chargement...</p>
      </div>
      <div v-else-if="charges.length === 0" class="text-center w-full h-[250px] flex flex-col justify-center items-center">
        <i class="fa-solid fa-file-invoice-dollar text-4xl text-gray-300 mb-4"></i>
        <h2 class="text-lg font-semibold text-gray-500">Aucune charge</h2>
      </div>

      <div v-else>
        <!-- Desktop -->
        <div class="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table class="w-full text-sm min-w-[760px]">
            <thead>
              <tr class="text-left text-gray-500 border-b border-gray-100">
                <th class="px-4 py-3 font-semibold">Date</th>
                <th class="px-4 py-3 font-semibold">Type</th>
                <th class="px-4 py-3 font-semibold">Concerné</th>
                <th class="px-4 py-3 font-semibold">Libellé</th>
                <th class="px-4 py-3 font-semibold text-right">Montant</th>
                <th class="px-4 py-3 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in charges" :key="c._id" class="border-b border-gray-50 hover:bg-gray-50">
                <td class="px-4 py-3 text-gray-600">{{ formatDate(c.date || c.createdAt) }}</td>
                <td class="px-4 py-3"><span :class="typeBadgeClass(c)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ typeBadgeLabel(c) }}</span></td>
                <td class="px-4 py-3 text-gray-700"><span v-if="c.type === 'employe'">{{ c.employe?.prenoms }} {{ c.employe?.nom }}</span><span v-else>{{ c.boutique || 'Entreprise' }}</span></td>
                <td class="px-4 py-3 text-gray-700">{{ c.libelle }}</td>
                <td class="px-4 py-3 text-right font-medium text-red-500">{{ formatMoney(c.montant) }}</td>
                <td class="px-4 py-3">
                  <div class="flex justify-center gap-2">
                    <button @click="openModal(c)" class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><i class="fa-solid fa-pen text-xs text-gray-600"></i></button>
                    <button @click="removeCharge(c._id)" class="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center"><i class="fa-solid fa-trash text-xs text-red-500"></i></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Mobile -->
        <div class="lg:hidden space-y-3">
          <div v-for="c in charges" :key="c._id" class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div class="flex justify-between items-start mb-2">
              <span :class="typeBadgeClass(c)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ typeBadgeLabel(c) }}</span>
              <span class="font-bold text-red-500">{{ formatMoney(c.montant) }}</span>
            </div>
            <p class="text-sm font-medium text-gray-800">{{ c.libelle }}</p>
            <p class="text-xs text-gray-500">{{ c.type === 'employe' ? (c.employe?.prenoms + ' ' + c.employe?.nom) : (c.boutique || 'Entreprise') }} • {{ formatDate(c.date || c.createdAt) }}</p>
            <div class="flex gap-2 mt-3">
              <button @click="openModal(c)" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200"><i class="fa-solid fa-pen text-xs mr-1"></i> Modifier</button>
              <button @click="removeCharge(c._id)" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-500 hover:bg-red-100"><i class="fa-solid fa-trash text-xs mr-1"></i> Supprimer</button>
            </div>
          </div>
        </div>
      </div>

      <!-- MODAL -->
      <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">{{ editingId ? 'Modifier la charge' : 'Ajouter une charge' }}</h3>
              <button @click="showModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <form @submit.prevent="saveCharge" class="space-y-3">
              <div v-if="isSuper">
                <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select v-model="form.type" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500">
                  <option value="globale">Charge boutique</option>
                  <option value="employe">Charge personnel (déduite du salaire)</option>
                </select>
              </div>
              <div v-if="isSuper && form.type === 'employe'">
                <label class="block text-sm font-medium text-gray-700 mb-1">Employé <span class="text-red-500">*</span></label>
                <select v-model="form.employeId" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
                  <option value="" disabled>Sélectionner</option>
                  <option v-for="e in employees" :key="e._id" :value="e._id">{{ e.firstname }} {{ e.lastname }}</option>
                </select>
              </div>
              <div v-else-if="isSuper">
                <label class="block text-sm font-medium text-gray-700 mb-1">Boutique</label>
                <select v-model="form.boutique" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500">
                  <option value="">Entreprise (toutes)</option>
                  <option v-for="b in boutiques" :key="b" :value="b">{{ b }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input v-model="form.date" type="date" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Libellé <span class="text-red-500">*</span></label>
                <input v-model="form.libelle" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Montant (FCFA) <span class="text-red-500">*</span></label>
                <input v-model="form.montant" type="number" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required>
              </div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center"><span v-if="isSubmitting" class="loader-small mr-2"></span> {{ editingId ? 'Modifier' : 'Ajouter' }}</button>
            </form>
          </div>
        </div>
      </div>

    </section>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import HeaderComponent from '../components/HeaderComponent.vue';
import axios from 'axios';
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const isSuper = computed(() => !!user.value?.isSuper);
const boutiques = ['Calavi', 'Parakou'];

// Formate en YYYY-MM-DD à partir des composantes locales (toISOString() décale la date
// d'un jour dans les fuseaux UTC+, ex: UTC+1 au Bénin).
function fmtLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function today() { return fmtLocal(new Date()); }
function monthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: fmtLocal(first), to: fmtLocal(last) };
}

const scopeOptions = [
  { value: 'toutes', label: 'Toutes les charges' },
  { value: 'Calavi', label: 'Calavi' },
  { value: 'Parakou', label: 'Parakou' },
  { value: 'personnel', label: 'Personnel' }
];

const charges = ref([]);
const employees = ref([]);
const isLoading = ref(true);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const filters = ref({ ...monthRange(), scope: 'toutes' });

const showModal = ref(false);
const editingId = ref(null);
const form = ref({ type: 'globale', employeId: '', boutique: '', libelle: '', montant: 0, date: today() });

// Personnel = bleu, Calavi/Parakou = leurs couleurs habituelles (cf. graphique Dashboard),
// Entreprise (charge boutique sans boutique précise) = orange.
function typeBadgeClass(c) {
  if (c.type === 'employe') return 'bg-blue-100 text-blue-700';
  if (c.boutique === 'Calavi') return 'bg-indigo-100 text-indigo-700';
  if (c.boutique === 'Parakou') return 'bg-green-100 text-green-700';
  return 'bg-orange-100 text-orange-700';
}
function typeBadgeLabel(c) {
  return c.type === 'employe' ? 'Personnel' : (c.boutique || 'Entreprise');
}

const formatMoney = (n) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0))} F`;
const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const totalCharges = computed(() => charges.value.reduce((a, c) => a + (Number(c.montant) || 0), 0));
const showSuccess = (m) => { successMessage.value = m; setTimeout(() => (successMessage.value = ''), 5000); };
const showError = (m) => { errorMessage.value = m; setTimeout(() => (errorMessage.value = ''), 8000); };

function buildParams() {
  const p = {};
  if (filters.value.from) p.from = filters.value.from;
  if (filters.value.to) p.to = filters.value.to;
  if (isSuper.value) {
    if (filters.value.scope === 'personnel') p.type = 'employe';
    else if (filters.value.scope === 'Calavi' || filters.value.scope === 'Parakou') { p.type = 'globale'; p.boutique = filters.value.scope; }
    // 'toutes' : pas de filtre type -> liste complète (charges boutique + personnel, toutes boutiques)
  }
  return p;
}

async function fetchCharges() {
  isLoading.value = true;
  try {
    const { data } = await axios.get('/api/charges', { params: buildParams() });
    charges.value = data || [];
  } catch (e) { showError('Erreur lors du chargement.'); }
  finally { isLoading.value = false; }
}
async function fetchEmployees() {
  if (!isSuper.value) return;
  try { const { data } = await axios.get('/api/users'); employees.value = data || []; } catch (e) { /* */ }
}
function setScope(v) { filters.value.scope = v; fetchCharges(); }
function resetFilters() { filters.value = { ...monthRange(), scope: 'toutes' }; fetchCharges(); }

function openModal(c = null) {
  if (c) {
    editingId.value = c._id;
    form.value = { type: c.type, employeId: c.employe?._id || '', boutique: c.boutique || '', libelle: c.libelle, montant: c.montant, date: (c.date || c.createdAt || '').slice(0, 10) };
  } else {
    editingId.value = null;
    form.value = { type: isSuper.value ? 'globale' : 'globale', employeId: '', boutique: '', libelle: '', montant: 0, date: today() };
  }
  showModal.value = true;
}

async function saveCharge() {
  isSubmitting.value = true;
  try {
    const payload = { libelle: form.value.libelle, montant: form.value.montant, date: form.value.date };
    if (isSuper.value) {
      payload.type = form.value.type;
      if (form.value.type === 'employe') {
        const emp = employees.value.find(e => e._id === form.value.employeId);
        if (!emp) { showError('Sélectionnez un employé.'); isSubmitting.value = false; return; }
        payload.employe = { _id: emp._id, nom: emp.lastname, prenoms: emp.firstname };
      } else {
        payload.boutique = form.value.boutique;
      }
    }
    // Pour un employé non-super, le backend force type=globale + sa boutique.
    if (editingId.value) { await axios.put(`/api/charges/${editingId.value}`, payload); showSuccess('Charge modifiée.'); }
    else { await axios.post('/api/charges', payload); showSuccess('Charge ajoutée.'); }
    showModal.value = false;
    fetchCharges();
  } catch (e) { showError(e.response?.data?.error || 'Erreur lors de l\'enregistrement.'); }
  finally { isSubmitting.value = false; }
}
async function removeCharge(id) {
  if (!confirm('Supprimer cette charge ?')) return;
  try { await axios.delete(`/api/charges/${id}`); showSuccess('Charge supprimée.'); fetchCharges(); }
  catch (e) { showError(e.response?.data?.error || 'Erreur lors de la suppression.'); }
}

onMounted(() => { fetchCharges(); fetchEmployees(); });
</script>

<style scoped>
.loader { border: 3px solid #f3f3f3; border-top: 3px solid #f97316; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite; }
.loader-small { border: 2px solid #f3f3f3; border-top: 2px solid #fff; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
